import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import VirtualSubNav from "../../components/VirtualWorld/VirtualSubNav";
import LockedFeature from "../../components/VirtualWorld/LockedFeature";
import GuideBanner from "../../components/VirtualWorld/GuideBanner";
import SeasonEndOverlay from "../../components/VirtualWorld/SeasonEndOverlay";
import CupCeremonyOverlay from "../../components/VirtualWorld/CupCeremonyOverlay";
import { clearGuideUrgencyTimers } from "../../components/VirtualWorld/GuideUrgencyTimer";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import useTransferOfferToasts from "../../hooks/useTransferOfferToasts";
import vw from "../../api/virtualWorld.api";
import { isVirtualWorldTestHost } from "../../helper/virtualWorldHost";
import {
  featureFromPath,
  nextFor,
  unlocksFor,
  worldNavFromPayloads,
} from "../../helper/virtualWorldProgression";

const ALL_ITEMS = [
  { to: "/manager", label: "Mein Club", emoji: "🏠", feature: "home", end: true },
  { to: "/manager/team", label: "Mannschaft", emoji: "🧑‍🤝‍🧑", feature: "team" },
  { to: "/manager/match", label: "Wettbewerbe", emoji: "🏆", feature: "match" },
  { to: "/manager/training", label: "Training", emoji: "🏋️", feature: "training" },
  { to: "/manager/inbox", label: "Inbox", emoji: "📬", feature: "inbox" },
  { to: "/manager/league", label: "Liga", emoji: "📊", feature: "league" },
  { to: "/manager/transfers", label: "Transfers", emoji: "🔁", feature: "transfers" },
  { to: "/manager/club", label: "Geschäftsstelle", emoji: "🏢", feature: "club" },
  { to: "/manager/venue", label: "Heimstätte", emoji: "🏟️", feature: "venue" },
  { to: "/manager/hall", label: "Ruhm", emoji: "🏛️", feature: "hall" },
];

const ManagerLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userId, career, manager, refresh, error } = useVirtualWorld();
  const [resetBusy, setResetBusy] = useState(false);
  const unread = manager?.inbox?.unread || 0;
  const nav = worldNavFromPayloads(career, manager);
  const unlocks = unlocksFor("manager", career, manager);
  useTransferOfferToasts(userId, Boolean(manager?.club) && unlocks.transfers !== false);
  const next = nextFor("manager", career, manager);
  const guide = manager?.guide || manager?.progression?.manager?.guide;
  const worldLocked = nav.manager === false && nav.hub === false;
  const feature = featureFromPath(location.pathname, "manager");
  const guided = Boolean(manager?.guided ?? manager?.progression?.manager?.guided ?? guide?.guided);
  const navAllow = new Set(guide?.navFeatures || []);
  // Während Begleitung: auch freigeschaltete Features sperren, die der Guide noch nicht zeigt
  // (z. B. Transfermarkt nach erstem Sieg, obwohl Schritt noch „Geschäftsstelle“ ist).
  const guideBlocksFeature =
    guided &&
    navAllow.size > 0 &&
    feature &&
    feature !== "home" &&
    !navAllow.has(feature) &&
    feature !== next?.key &&
    feature !== guide?.current?.feature;
  const featureLocked =
    Boolean(manager?.club) &&
    feature &&
    feature !== "home" &&
    (!unlocks[feature] || guideBlocksFeature);
  const showTempReset = isVirtualWorldTestHost();
  const starterCount = (manager?.club?.starterKeys || []).length;
  const lineupShort = Boolean(manager?.club) && !guided && starterCount < 4;

  const items = ALL_ITEMS
    .filter((item) => {
      if (item.feature === "home") return true;
      if (!unlocks[item.feature] && item.feature !== next?.key) return false;
      if (guided && navAllow.size) {
        return navAllow.has(item.feature) || item.feature === next?.key || item.feature === guide?.current?.feature;
      }
      return unlocks[item.feature] || item.feature === next?.key;
    })
    .map((item) => ({
      ...item,
      badge: item.feature === "inbox" ? unread : 0,
      locked: item.feature !== "home" && !unlocks[item.feature],
    }));

  const resetAll = async () => {
    if (!userId || resetBusy) return;
    if (!window.confirm("TEMP: Manager komplett zurücksetzen? Club, Matches und Onboarding weg — dann neu gründen.")) {
      return;
    }
    const typed = window.prompt('Zum Bestätigen RESET tippen (sonst Abbruch):');
    if (String(typed || "").trim().toUpperCase() !== "RESET") {
      window.alert("Reset abgebrochen.");
      return;
    }
    setResetBusy(true);
    try {
      await vw.resetAll(userId);
      clearGuideUrgencyTimers();
      await refresh();
      navigate("/manager", { replace: true });
    } catch (err) {
      window.alert(err?.data?.message || err?.message || "Reset fehlgeschlagen.");
    } finally {
      setResetBusy(false);
    }
  };

  const club = manager?.club;
  const budget = Number(club?.budget ?? manager?.dashboard?.finance?.budget) || 0;
  const statusLabel =
    club?.statusLabel || manager?.venue?.statusLabel || manager?.venue?.tier?.label || null;
  const venueLabel = manager?.venue?.tier?.label || null;

  return (
    <div className={`px-8 text-black dark:text-white ${guided ? "py-6" : "py-12"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <h1 className={`font-bold uppercase ${guided ? "text-2xl opacity-70" : "text-5xl"}`}>
            {guided ? clubNameHeader(manager) : "Manager"}
          </h1>
          {club && guided ? (
            <div className="mt-2 text-2xl md:text-3xl font-black tracking-tight text-amber-700 dark:text-amber-300">
              {manager.calendar?.label || "Vorsaison · Freundschaft"}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          {club ? (
            <div className="flex flex-wrap gap-2 text-sm">
              {(() => {
                const clubNavOk =
                  !guided ||
                  !navAllow.size ||
                  navAllow.has("club") ||
                  guide?.current?.feature === "club";
                const budgetClass =
                  "rounded-xl border border-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1.5 font-bold";
                const budgetInner = <>💶 {budget.toLocaleString("de-DE")} €</>;
                return clubNavOk ? (
                  <Link
                    to="/manager/club"
                    className={`${budgetClass} hover:ring-2 hover:ring-emerald-300/50 transition`}
                    title="Zur Geschäftsstelle"
                  >
                    {budgetInner}
                  </Link>
                ) : (
                  <span className={budgetClass} title="Geschäftsstelle öffnet im passenden Begleit-Schritt">
                    {budgetInner}
                  </span>
                );
              })()}
              {statusLabel || venueLabel ? (
                (() => {
                  const venueNavOk =
                    !guided ||
                    !navAllow.size ||
                    navAllow.has("venue") ||
                    guide?.current?.feature === "venue";
                  const venueClass =
                    "rounded-xl border border-amber-400 bg-amber-50 dark:bg-amber-950/40 px-3 py-1.5 font-semibold";
                  const venueInner = (
                    <>
                      🏟️ {statusLabel || venueLabel}
                      {statusLabel && venueLabel && statusLabel !== venueLabel ? (
                        <span className="opacity-70 font-normal"> · {venueLabel}</span>
                      ) : null}
                    </>
                  );
                  return venueNavOk && unlocks.venue ? (
                    <Link
                      to="/manager/venue"
                      className={`${venueClass} hover:ring-2 hover:ring-amber-300/50 transition`}
                      title="Zur Heimstätte"
                    >
                      {venueInner}
                    </Link>
                  ) : (
                    <span className={venueClass} title="Heimstätte folgt mit der Geschäftsstelle">
                      {venueInner}
                    </span>
                  );
                })()
              ) : null}
            </div>
          ) : null}
          {showTempReset ? (
            <button
              type="button"
              disabled={resetBusy || !userId}
              onClick={resetAll}
              className="shrink-0 px-3 py-1.5 rounded-lg bg-red-700/90 text-white text-xs font-bold border border-red-300/50 opacity-80"
              title="Nur Test-Host — Onboarding neu starten"
            >
              {resetBusy ? "Reset …" : "TEMP · Reset"}
            </button>
          ) : null}
        </div>
      </div>
      {error && !manager?.club ? (
        <p className="mb-3 text-sm text-red-600 font-semibold">Manager-API: {error}</p>
      ) : null}
      {next && manager?.club && !guided && next.key !== "home" && next.title !== "Alles erklärt" ? (
        <p className="mb-2 opacity-80">Nächster Schritt: {next.title}.</p>
      ) : null}
      {manager?.club ? <VirtualSubNav items={items} /> : null}
      {lineupShort ? (
        <div className="mb-4 rounded-2xl border border-amber-400 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm">
            <strong>Starting Four: {starterCount}/4</strong> — Mit weniger als 4 aufgestellten
            Starters bleiben Wettbewerbe ausgeblendet bzw. nicht spielbar. Kadergröße reicht nicht.
          </p>
          <Link
            to="/manager/team"
            className="shrink-0 px-4 py-2 rounded-xl bg-amber-700 text-amber-50 text-sm font-bold"
          >
            Aufstellung setzen
          </Link>
        </div>
      ) : null}
      {guided && manager?.club && !worldLocked && !featureLocked ? <GuideBanner /> : null}
      {manager?.seasonCeremony ? (
        <SeasonEndOverlay
          userId={userId}
          ceremony={manager.seasonCeremony}
          review={manager.seasonReview}
          onRefresh={refresh}
          onDone={() => {
            // Nach Ack: lokale Manager-Zeremonie sofort verwerfen (Reconstruct-Race)
            void refresh();
          }}
        />
      ) : null}
      {!manager?.seasonCeremony && manager?.cupCeremony ? (
        <CupCeremonyOverlay
          userId={userId}
          ceremony={manager.cupCeremony}
          onRefresh={refresh}
          onDone={() => refresh()}
        />
      ) : null}
      {worldLocked || featureLocked ? (
        <LockedFeature feature={feature} next={worldLocked ? null : next} worldLocked={worldLocked} otherWorld="/career" />
      ) : (
        <Outlet />
      )}
    </div>
  );
};

function clubNameHeader(manager) {
  return manager?.club?.name || "Mein Club";
}

export default ManagerLayout;
